import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Phone, Menu, X, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useShop } from '../context/ShopContext';
import { buildLoginUrl } from '../utils/safeRedirect';

const API_URL = process.env.REACT_APP_API_URL;

export default function Header() {
    const location = useLocation();
    const loginNext = ['/cart', '/checkout'].includes(location.pathname) ? location.pathname : null;
    const loginHref = buildLoginUrl(loginNext);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { itemCount } = useCart();
    const { isAuthenticated, user, logout } = useAuth();
    const { shopName, logoUrl } = useShop();

    return (
        <header
            className="sticky top-0 z-50 glass pt-[env(safe-area-inset-top,0px)]"
            data-testid="storefront-header"
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between min-h-[3.25rem] sm:min-h-[4rem] py-1.5 sm:py-0">
                    <Link to="/" className="flex items-center gap-2 min-w-0 touch-manipulation -ml-0.5 sm:ml-0 pr-2">
                        {logoUrl ? (
                            <img
                                src={logoUrl.startsWith('/api') ? `${API_URL.replace('/api', '')}${logoUrl}` : logoUrl}
                                alt={shopName}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover shrink-0"
                            />
                        ) : (
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full gradient-hero flex items-center justify-center shrink-0">
                                <ShoppingBag className="w-4 h-4 text-white" />
                            </div>
                        )}
                        <span className="font-display text-base sm:text-xl font-semibold text-primary truncate">
                            {shopName}
                        </span>
                    </Link>

                    <div className="relative flex items-center gap-2 sm:gap-3">
                        {/* Desktop links */}
                        <Link to="/contact" className="hidden sm:inline-flex">
                            <Button variant="ghost" size="sm" className="rounded-full" data-testid="contact-btn">
                                <Phone className="w-4 h-4 mr-1.5" />
                                <span>Επικοινωνία</span>
                            </Button>
                        </Link>

                        <Link to="/favorites" className="hidden sm:inline-flex" title="Αγαπημένα">
                            <Button variant="ghost" size="sm" className="rounded-full" data-testid="favorites-btn">
                                <Heart className="w-4 h-4 mr-1.5" />
                                <span>Αγαπημένα</span>
                            </Button>
                        </Link>

                        <Link to="/cart" className="touch-manipulation">
                            <Button variant="outline" size="sm" className="rounded-full relative h-10 px-3 sm:h-9" data-testid="cart-btn">
                                <ShoppingBag className="w-4 h-4 mr-1.5" />
                                <span className="hidden sm:inline">Καλάθι</span>
                                {itemCount > 0 && (
                                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                                        {itemCount}
                                    </span>
                                )}
                            </Button>
                        </Link>

                        {isAuthenticated ? (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link to="/account">
                                    <Button variant="ghost" size="sm" data-testid="account-btn">
                                        <User className="w-4 h-4 mr-1" />
                                        <span className="hidden sm:inline">{user?.name}</span>
                                    </Button>
                                </Link>
                                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                                    Αποσύνδεση
                                </Button>
                            </div>
                        ) : (
                            <Link to={loginHref} className="hidden sm:inline-flex">
                                <Button variant="ghost" size="sm" data-testid="login-btn">
                                    <User className="w-4 h-4 mr-1" />
                                    Σύνδεση
                                </Button>
                            </Link>
                        )}

                        {/* Mobile burger: Contact + Auth links */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="sm:hidden"
                            aria-label="Menu"
                            data-testid="storefront-menu-btn"
                            onClick={() => setMobileMenuOpen((prev) => !prev)}
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </Button>

                        {mobileMenuOpen && (
                            <>
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    aria-label="Κλείσιμο μενού"
                                    className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px] sm:hidden"
                                    onClick={() => setMobileMenuOpen(false)}
                                />
                                <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[min(18rem,calc(100vw-1.5rem))] rounded-2xl border border-border bg-background/95 backdrop-blur-md shadow-xl p-2 sm:hidden">
                                <Link
                                    to="/contact"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-foreground hover:bg-muted active:bg-muted/80 touch-manipulation"
                                >
                                    <Phone className="w-4 h-4" />
                                    Επικοινωνία
                                </Link>
                                <Link
                                    to="/favorites"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-foreground hover:bg-muted active:bg-muted/80 touch-manipulation"
                                    data-testid="favorites-mobile-link"
                                >
                                    <Heart className="w-4 h-4" />
                                    Αγαπημένα
                                </Link>
                                {isAuthenticated ? (
                                    <>
                                        <Link
                                            to="/account"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-foreground hover:bg-muted active:bg-muted/80 touch-manipulation"
                                        >
                                            <User className="w-4 h-4" />
                                            Λογαριασμός
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMobileMenuOpen(false);
                                                logout();
                                            }}
                                            className="w-full text-left flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-foreground hover:bg-muted active:bg-muted/80 touch-manipulation"
                                        >
                                            <User className="w-4 h-4" />
                                            Αποσύνδεση
                                        </button>
                                    </>
                                ) : (
                                    <Link
                                        to={loginHref}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm text-foreground hover:bg-muted active:bg-muted/80 touch-manipulation"
                                    >
                                        <User className="w-4 h-4" />
                                        Σύνδεση
                                    </Link>
                                )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
